import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowRight, RefreshCw, X, Tag } from "lucide-react";
import { ListenButton } from "./ListenButton";
import { ReadingTime } from "./ReadingTime";
import { ArticleAudioPlayer } from "./ArticleAudioPlayer";

// A curated narrative flow — each essay gently leads to the next theme, so the
// Journal reads as one unfolding story rather than a list of pages.
const NEXT_FLOW = {
  "art-human-flourishing": "art-lifestyle-medicine",
  "art-lifestyle-medicine": "art-beauty-culture",
  "art-beauty-culture": "art-lifelong-learning",
  "art-lifelong-learning": "art-technology",
  "art-technology": "art-character-purpose",
  "art-character-purpose": "art-human-flourishing",
};

export default function Journal() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const response = await axios.get(`${backendUrl}/api/articles?lang=${lang}`);
        setArticles(response.data);
      } catch (error) {
        console.error("Error loading articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [lang]);

  useEffect(() => {
    const articleId = searchParams.get("article");
    if (articleId && articles.length > 0) {
      const art = articles.find(a => a.id === articleId);
      setSelectedArticle(art || null);
    } else {
      setSelectedArticle(null);
    }
  }, [searchParams, articles]);

  const handleOpenArticle = (art) => setSearchParams({ article: art.id });
  const handleCloseArticle = () => { setSearchParams({}); setSelectedArticle(null); };

  useEffect(() => {
    if (selectedArticle && overlayRef.current) overlayRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedArticle]);

  const categoryList = t("journal.categories", { returnObjects: true }) || [];
  const categories = [{ key: "ALL", label: t("journal.all") }, ...categoryList];

  const filteredArticles = filter === "ALL"
    ? articles
    : articles.filter(art => art.categoryKey === filter);

  return (
    <div className="py-12 space-y-20 relative">

      {/* Page Header */}
      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("journal.eyebrow")}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">
          {t("journal.heading")}
        </h1>
        <p className="font-serif text-base sm:text-lg text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">
          {t("journal.subtitle")}
        </p>
        <div className="flex justify-center pt-2">
          <ListenButton text={t("journal.listenText")} testId="journal-page-listen" idleKey="listen.toPage" />
        </div>
      </section>

      {/* Category Filter bar */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-center gap-4 border-y border-subtle-custom py-6">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setFilter(cat.key)}
              data-testid={`category-btn-${cat.key.toLowerCase()}`}
              className={`text-[9px] font-sans tracking-[0.2em] uppercase py-2 px-4 transition-all duration-300 rounded-none border ${
                filter === cat.key
                  ? "bg-primary-custom text-[var(--accent-bronze)] border-[var(--accent-bronze)]"
                  : "bg-surface-custom border-transparent text-secondary-custom hover:text-primary-custom"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Main Journal Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="py-32 text-center text-secondary-custom flex flex-col items-center space-y-4">
            <RefreshCw className="w-8 h-8 animate-spin text-[var(--accent-bronze)]" />
            <span className="font-serif italic text-sm">{t("journal.opening")}</span>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="py-32 text-center text-secondary-custom">
            <p className="font-serif text-base italic">{t("journal.empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {filteredArticles.map((art) => (
              <div key={art.id} data-testid={`article-card-${art.id}`} className="flex flex-col space-y-6 group border border-subtle-custom p-6 bg-surface-custom/20 hover:border-[var(--accent-bronze)] transition-all duration-500 cursor-pointer" onClick={() => handleOpenArticle(art)}>
                <div className="aspect-[16/10] overflow-hidden border border-subtle-custom relative">
                  {art.video ? (
                    <video src={art.video} poster={art.image} muted loop playsInline autoPlay={typeof window!=='undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches} preload="metadata" aria-label={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" />
                  ) : (
                    <img src={art.image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" />
                  )}
                  {art.credit && (
                    <span data-testid={`article-credit-${art.id}`} className="absolute bottom-2 right-2 z-10 text-[9px] font-sans tracking-[0.15em] uppercase text-white/90 bg-black/40 backdrop-blur-[2px] px-2 py-1 pointer-events-none">{art.credit}</span>
                  )}
                </div>
                <div className="flex-grow space-y-4 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 text-[9px] font-sans tracking-wide text-secondary-custom uppercase">
                      <span>{art.author}</span>
                    </div>
                    <h3 className="text-xl font-serif tracking-tight text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{art.title}</h3>
                    <p className="font-sans text-xs text-secondary-custom leading-relaxed">{art.summary}</p>
                  </div>
                  <div className="pt-4 border-t border-subtle-custom flex justify-between items-center text-[10px] font-sans tracking-widest uppercase text-[var(--accent-bronze)]">
                    <span>{t("journal.readEssay")}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reading Overlay */}
      {selectedArticle && (
        <div data-testid="article-reading-overlay" className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-end animate-fade-in transition-all">
          <div ref={overlayRef} className="w-full max-w-3xl h-full bg-primary-custom shadow-2xl border-l border-subtle-custom flex flex-col justify-between overflow-y-auto animate-slide-left p-6 md:p-12 space-y-10">
            <div className="flex justify-between items-center border-b border-subtle-custom pb-6">
              <div className="flex items-center space-x-3 text-[10px] font-sans tracking-widest text-[var(--accent-bronze)] uppercase">
                <Tag className="w-3.5 h-3.5" />
                <span>{selectedArticle.category}</span>
              </div>
              <button onClick={handleCloseArticle} data-testid="close-article-overlay" className="p-2 border border-subtle-custom hover:bg-surface-custom text-secondary-custom hover:text-primary-custom transition-all duration-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-8 flex-grow">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-serif text-primary-custom tracking-tight leading-tight">{selectedArticle.title}</h2>
                <div className="flex items-center space-x-4 text-xs font-sans text-secondary-custom uppercase tracking-wider">
                  <span>{selectedArticle.author}</span>
                  <ReadingTime text={selectedArticle.content} testId="journal-article-reading-time" pageType="journal_article" pageSlug={selectedArticle.id} />
                </div>
                <div className="pt-2">
                  <ArticleAudioPlayer
                    text={`${selectedArticle.title}. ${selectedArticle.content}`}
                    pageType="journal_article"
                    pageSlug={selectedArticle.id}
                    testId="article-listen-button"
                  />
                </div>
              </div>
              <div className="aspect-[16/9] overflow-hidden border border-subtle-custom relative">
                {selectedArticle.video ? (
                  <video src={selectedArticle.video} poster={selectedArticle.image} muted loop playsInline autoPlay={typeof window!=='undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches} preload="metadata" aria-label={selectedArticle.title} className="w-full h-full object-cover" />
                ) : (
                  <img src={selectedArticle.image} alt={selectedArticle.title} className="w-full h-full object-cover" />
                )}
                {selectedArticle.credit && (
                  <span data-testid={`article-credit-overlay-${selectedArticle.id}`} className="absolute bottom-2 right-2 z-10 text-[9px] font-sans tracking-[0.15em] uppercase text-white/90 bg-black/40 backdrop-blur-[2px] px-2 py-1 pointer-events-none">{selectedArticle.credit}</span>
                )}
              </div>
              <div className="font-serif text-base sm:text-lg text-primary-custom leading-relaxed space-y-6 font-light">
                <p className="first-letter:text-5xl first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:font-medium first-letter:text-[var(--accent-bronze)]">{selectedArticle.content}</p>
                <p className="italic border-l-2 border-[var(--accent-bronze)] pl-4 text-secondary-custom my-8">&quot;{selectedArticle.summary}&quot;</p>
                <p>{t("journal.overlayClose")}</p>
              </div>
            </div>

            {/* Continue the Journey — an editorial invitation onward, never a dead-end */}
            {(() => {
              const nextArt = articles.find((a) => a.id === NEXT_FLOW[selectedArticle.id]);
              if (!nextArt) return null;
              const invites = t("journal.continue.eyebrows", { returnObjects: true }) || [];
              const idx = articles.findIndex((a) => a.id === selectedArticle.id);
              const invite = invites.length ? invites[(idx >= 0 ? idx : 0) % invites.length] : "";
              return (
                <div className="border-t border-subtle-custom pt-10" data-testid="continue-journey">
                  <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block mb-6">{invite}</span>
                  <button onClick={() => handleOpenArticle(nextArt)} data-testid="continue-journey-next" className="group w-full grid grid-cols-1 sm:grid-cols-2 border border-subtle-custom hover:border-[var(--accent-bronze)] transition-all duration-500 overflow-hidden text-left">
                    <div className="aspect-[16/10] sm:aspect-auto min-h-[180px] overflow-hidden relative">
                      {nextArt.video ? (
                        <video src={nextArt.video} poster={nextArt.image} muted loop playsInline autoPlay={typeof window!=='undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches} preload="metadata" aria-label={nextArt.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <img src={nextArt.image} alt={nextArt.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      )}
                      {nextArt.credit && (
                        <span data-testid={`article-credit-next-${nextArt.id}`} className="absolute bottom-2 right-2 z-10 text-[9px] font-sans tracking-[0.15em] uppercase text-white/90 bg-black/40 backdrop-blur-[2px] px-2 py-1 pointer-events-none">{nextArt.credit}</span>
                      )}
                    </div>
                    <div className="p-7 flex flex-col justify-center space-y-3">
                      <span className="text-[9px] font-sans tracking-[0.25em] uppercase text-secondary-custom">{nextArt.category}</span>
                      <h3 className="text-xl font-serif text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors leading-snug">{nextArt.title}</h3>
                      <p className="font-sans text-xs text-secondary-custom leading-relaxed">{nextArt.summary}</p>
                      <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold pt-1">{t("journal.continue.cta")}<ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" /></span>
                    </div>
                  </button>
                </div>
              );
            })()}

            <div className="border-t border-subtle-custom pt-8 flex items-center justify-between text-xs font-sans text-secondary-custom uppercase tracking-wider">
              <span>The Thriving Life 101</span>
              <span>© 2026</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
